/**
 * Google-style multi-token smart search with field-weighted scoring,
 * fuzzy matching, cross-field spread bonuses, and full highlight support.
 *
 * Usage:
 *   smartSearch(matters, "smith auto", [
 *     (m) => ({ value: m.matter, weight: 1.5 }),
 *     (m) => ({ value: m.client, weight: 1.2 }),
 *     (m) => ({ value: m.description }),
 *   ]);
 */

export type SearchableField = {
  value: string;
  weight?: number;
};

export type MatchRange = {
  start: number;
  end: number;
};

export type SearchResult<T> = {
  item: T;
  score: number;
  /** Matched character ranges within the display label (for highlighting) */
  matches: Map<number, MatchRange[]>;
  /** Tokens that matched, for display-label re-highlighting */
  matchedTokens: string[];
};

// ── scoring constants ──────────────────────────────────────────────
const SCORE_EXACT = 100; // entire field equals a single token
const SCORE_WORD_START = 60; // token matches at a word boundary
const SCORE_FIELD_START = 40; // token matches at the start of the field
const SCORE_CONTAINS = 20; // token found as a substring somewhere
const SCORE_FUZZY = 10; // fuzzy match (1 edit distance)

const BONUS_FIELD_SPREAD = 15; // bonus when tokens spread across multiple fields
const BONUS_ADJACENCY = 10; // bonus when tokens appear near each other in a field

// fuzzy matching kicks in for tokens of this length or longer
const FUZZY_MIN_TOKEN_LENGTH = 4;
// max edit distance for fuzzy matching
const FUZZY_MAX_DISTANCE = 1;

// ── Fuzzy matching (Damerau-Levenshtein) ───────────────────────────

/**
 * Damerau-Levenshtein distance between two strings.
 * Supports insertions, deletions, substitutions, and transpositions.
 * Returns early if distance exceeds maxDist.
 */
function damerauLevenshtein(a: string, b: string, maxDist: number): number {
  const m = a.length;
  const n = b.length;

  // Quick bounds check
  if (Math.abs(m - n) > maxDist) return maxDist + 1;
  if (m === 0) return n;
  if (n === 0) return m;

  // Use two rows + prev row for transposition check
  let prevPrev = new Array(n + 1);
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);

  for (let j = 0; j <= n; j++) prev[j] = j;

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    let rowMin = i;

    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1, // deletion
        curr[j - 1] + 1, // insertion
        prev[j - 1] + cost, // substitution
      );
      // transposition
      if (
        i > 1 &&
        j > 1 &&
        a[i - 1] === b[j - 2] &&
        a[i - 2] === b[j - 1]
      ) {
        curr[j] = Math.min(curr[j], prevPrev[j - 2] + cost);
      }
      if (curr[j] < rowMin) rowMin = curr[j];
    }

    // Early termination if minimum in row exceeds maxDist
    if (rowMin > maxDist) return maxDist + 1;

    const tmp = prevPrev;
    prevPrev = prev;
    prev = curr;
    curr = tmp;
  }

  return prev[n];
}

/**
 * Find the best fuzzy match of `token` against whole words in `fieldLower`.
 * Only checks word boundaries (not every substring) for performance.
 */
function fuzzyFind(
  fieldLower: string,
  token: string,
  maxDist: number,
): { start: number; end: number; distance: number } | null {
  // Split field into words and check each one — O(words) not O(fieldLength)
  const wordRe = /[a-z0-9]+/gi;
  let best: { start: number; end: number; distance: number } | null = null;
  let match: RegExpExecArray | null;

  while ((match = wordRe.exec(fieldLower)) !== null) {
    const word = match[0];
    // Quick length check: skip words too different in length
    if (Math.abs(word.length - token.length) > maxDist) continue;

    const dist = damerauLevenshtein(token, word, maxDist);
    if (dist <= maxDist && (best === null || dist < best.distance)) {
      best = { start: match.index, end: match.index + word.length, distance: dist };
      if (dist === 0) return best;
    }
  }

  return best;
}

// ── Token scoring ──────────────────────────────────────────────────

/**
 * Score a single token against a single field value.
 * Returns { score, ranges } where ranges are the character indices that matched.
 */
function scoreToken(
  fieldValue: string,
  fieldLower: string,
  token: string,
): { score: number; ranges: MatchRange[] } {
  // exact match – the whole field is the token
  if (fieldLower === token) {
    return {
      score: SCORE_EXACT,
      ranges: [{ start: 0, end: fieldValue.length }],
    };
  }

  // word-boundary match – token appears right after a space / punctuation / start
  const wordBoundaryRe = new RegExp(
    `(?:^|[\\s\\-_/|,.;:()\\[\\]])${escapeRegex(token)}`,
    "gi",
  );
  const wbMatch = wordBoundaryRe.exec(fieldLower);
  if (wbMatch) {
    const offset = wbMatch[0].length - token.length;
    const start = wbMatch.index + offset;
    return {
      score: SCORE_WORD_START,
      ranges: [{ start, end: start + token.length }],
    };
  }

  // starts-with – token is a prefix of the whole field
  if (fieldLower.startsWith(token)) {
    return {
      score: SCORE_FIELD_START,
      ranges: [{ start: 0, end: token.length }],
    };
  }

  // substring – token exists somewhere
  const idx = fieldLower.indexOf(token);
  if (idx !== -1) {
    return {
      score: SCORE_CONTAINS,
      ranges: [{ start: idx, end: idx + token.length }],
    };
  }

  // fuzzy match – allow typos for longer tokens
  if (token.length >= FUZZY_MIN_TOKEN_LENGTH) {
    const fuzzy = fuzzyFind(fieldLower, token, FUZZY_MAX_DISTANCE);
    if (fuzzy) {
      return {
        score: SCORE_FUZZY,
        ranges: [{ start: fuzzy.start, end: fuzzy.end }],
      };
    }
  }

  return { score: 0, ranges: [] };
}

// ── Main search ────────────────────────────────────────────────────

/**
 * Multi-token, multi-field smart search.
 *
 * - Query is split on whitespace into tokens.
 * - Every token must match at least one field (AND logic).
 * - Each token picks the highest-scoring field it matches.
 * - Field weights multiply the raw score.
 * - Bonuses for token spread across fields and adjacency.
 * - Results are sorted descending by total score then capped at `limit`.
 */
export function smartSearch<T>(
  items: T[],
  query: string,
  fields: ((item: T) => SearchableField)[],
  options?: { limit?: number; threshold?: number },
): SearchResult<T>[] {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const tokens = trimmed.toLowerCase().split(/\s+/);
  const limit = options?.limit ?? 50;
  const threshold = options?.threshold ?? 0;

  const results: SearchResult<T>[] = [];

  for (const item of items) {
    // Pre-compute field values once per item
    const fieldValues = fields.map((fn) => fn(item));
    const fieldLowers = fieldValues.map((f) => (f.value ?? '').toLowerCase());

    let totalScore = 0;
    let allTokensMatched = true;
    const mergedMatches = new Map<number, MatchRange[]>();
    const matchedFields = new Set<number>();

    for (const token of tokens) {
      let bestTokenScore = 0;
      let bestFieldIdx = -1;
      let bestRanges: MatchRange[] = [];

      for (let fi = 0; fi < fieldValues.length; fi++) {
        const weight = fieldValues[fi].weight ?? 1;
        const { score, ranges } = scoreToken(
          fieldValues[fi].value ?? '',
          fieldLowers[fi],
          token,
        );
        const weighted = score * weight;

        if (weighted > bestTokenScore) {
          bestTokenScore = weighted;
          bestFieldIdx = fi;
          bestRanges = ranges;
        }
      }

      if (bestTokenScore === 0) {
        allTokensMatched = false;
        break;
      }

      totalScore += bestTokenScore;
      matchedFields.add(bestFieldIdx);

      // Merge ranges for this field
      if (bestFieldIdx >= 0) {
        const existing = mergedMatches.get(bestFieldIdx) ?? [];
        existing.push(...bestRanges);
        mergedMatches.set(bestFieldIdx, existing);
      }
    }

    if (!allTokensMatched || totalScore <= threshold) continue;

    // Bonus: tokens spread across multiple fields
    if (matchedFields.size > 1) {
      totalScore += BONUS_FIELD_SPREAD * (matchedFields.size - 1);
    }

    // Bonus: adjacency — tokens appearing near each other in same field
    for (const [, ranges] of mergedMatches) {
      if (ranges.length < 2) continue;
      const sorted = [...ranges].sort((a, b) => a.start - b.start);
      for (let i = 1; i < sorted.length; i++) {
        const gap = sorted[i].start - sorted[i - 1].end;
        if (gap >= 0 && gap <= 5) {
          totalScore += BONUS_ADJACENCY;
        }
      }
    }

    results.push({
      item,
      score: totalScore,
      matches: mergedMatches,
      matchedTokens: tokens,
    });
  }

  results.sort((a, b) => b.score - a.score);
  return results.slice(0, limit);
}

// ── Highlight helpers ─────────────────────────────────────────────

/**
 * Find ALL occurrences of each token in `text` and return highlight ranges.
 * This is used by the ComboBox renderer to highlight against the display label
 * (which may differ from the individual field values used for scoring).
 */
export function highlightTokensInText(
  text: string,
  tokens: string[],
): MatchRange[] {
  if (!tokens.length || !text) return [];

  const lower = text.toLowerCase();
  const ranges: MatchRange[] = [];

  for (const token of tokens) {
    let found = false;
    let searchFrom = 0;
    while (searchFrom < lower.length) {
      const idx = lower.indexOf(token, searchFrom);
      if (idx === -1) break;
      ranges.push({ start: idx, end: idx + token.length });
      searchFrom = idx + 1;
      found = true;
    }

    // If no exact substring found, try fuzzy for longer tokens
    if (
      !found &&
      token.length >= FUZZY_MIN_TOKEN_LENGTH
    ) {
      const fuzzy = fuzzyFind(lower, token, FUZZY_MAX_DISTANCE);
      if (fuzzy) {
        ranges.push({ start: fuzzy.start, end: fuzzy.end });
      }
    }
  }

  return ranges;
}

/**
 * Splits `text` into alternating plain / matched segments based on ranges.
 * Returns an array of { text, highlighted } objects that can be rendered
 * with React (wrap highlighted ones in <mark>).
 */
export function splitByMatches(
  text: string,
  ranges: MatchRange[],
): { text: string; highlighted: boolean }[] {
  if (!ranges.length) return [{ text, highlighted: false }];

  // Sort and merge overlapping ranges
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: MatchRange[] = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const prev = merged[merged.length - 1];
    if (sorted[i].start <= prev.end) {
      prev.end = Math.max(prev.end, sorted[i].end);
    } else {
      merged.push({ ...sorted[i] });
    }
  }

  const parts: { text: string; highlighted: boolean }[] = [];
  let cursor = 0;
  for (const range of merged) {
    if (cursor < range.start) {
      parts.push({ text: text.slice(cursor, range.start), highlighted: false });
    }
    parts.push({
      text: text.slice(range.start, range.end),
      highlighted: true,
    });
    cursor = range.end;
  }
  if (cursor < text.length) {
    parts.push({ text: text.slice(cursor), highlighted: false });
  }
  return parts;
}

// ── utils ─────────────────────────────────────────────────────────

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
